import React, { useState, useMemo } from 'react';

const TPS_RATE = 0.05;
const TVQ_RATE = 0.09975;

// ── PRINT / PDF ───────────────────────────────────────────────────────────────
export function printInvoice(inv) {
  const fmtCA = (n) =>
    (parseFloat(n) || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const today = new Date().toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });

  // Normalise legacy invoices (no lines array)
  const lines = inv.lines?.length
    ? inv.lines
    : inv.description
      ? [{ desc: inv.description, qty: 1, unitPrice: inv.amount || 0 }]
      : [];

  const subtotal  = typeof inv.subtotal  === 'number' ? inv.subtotal  : (inv.amount || 0);
  const tpsAmt    = typeof inv.tps       === 'number' ? inv.tps       : (inv.taxable ? subtotal * TPS_RATE : 0);
  const tvqAmt    = typeof inv.tvq       === 'number' ? inv.tvq       : (inv.taxable ? subtotal * TVQ_RATE : 0);
  const total     = typeof inv.total     === 'number' ? inv.total     : subtotal + tpsAmt + tvqAmt;

  const INVOICE_STATUS_LABELS = { payé: 'PAYÉ', en_attente: 'EN ATTENTE', en_retard: 'EN RETARD' };
  const INVOICE_STATUS_COLORS = { payé: '#16a34a', en_attente: '#d97706', en_retard: '#dc2626' };
  const statusLabel = INVOICE_STATUS_LABELS[inv.status] || inv.status || 'EN ATTENTE';
  const statusColor = INVOICE_STATUS_COLORS[inv.status] || '#d97706';

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8"/>
  <title>Facture ${inv.number || ''}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;font-size:13px;color:#111;background:#fff;padding:48px 56px;line-height:1.6;max-width:860px;margin:0 auto}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:48px}
    .logo{font-size:22px;font-weight:900;letter-spacing:-0.04em;color:#000}
    .logo span{color:#222}
    .inv-meta{text-align:right}
    .inv-meta h2{font-size:28px;font-weight:900;letter-spacing:-0.03em;color:#000;margin-bottom:6px}
    .inv-meta .number{font-size:14px;color:#666;margin-bottom:4px}
    .inv-meta .date{font-size:12px;color:#888}
    .status-badge{display:inline-block;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:800;letter-spacing:0.08em;color:#fff;background:${statusColor};margin-top:8px}
    .two-col{display:flex;gap:40px;margin-bottom:40px}
    .two-col>div{flex:1}
    .section-label{font-size:10px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#999;margin-bottom:8px}
    .client-name{font-size:15px;font-weight:700;color:#000;margin-bottom:3px}
    .client-detail{font-size:12px;color:#555;line-height:1.8}
    .from-info{font-size:12px;color:#555;line-height:1.8}
    table{width:100%;border-collapse:collapse;margin-bottom:28px}
    thead tr{border-bottom:2px solid #000}
    thead th{padding:10px 12px;text-align:left;font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#555}
    thead th:last-child,thead th:nth-child(2),thead th:nth-child(3){text-align:right}
    tbody tr{border-bottom:1px solid #eee}
    tbody tr:last-child{border-bottom:none}
    tbody td{padding:10px 12px;font-size:13px;color:#111;vertical-align:top}
    tbody td:nth-child(2),tbody td:nth-child(3),tbody td:nth-child(4){text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
    .totals{margin-left:auto;width:300px}
    .totals-row{display:flex;justify-content:space-between;padding:5px 0;font-size:13px;color:#555}
    .totals-row span:last-child{font-variant-numeric:tabular-nums;font-weight:600;color:#111}
    .totals-divider{border:none;border-top:2px solid #000;margin:8px 0}
    .totals-total{display:flex;justify-content:space-between;padding:6px 0;font-size:18px;font-weight:900;color:#000}
    .totals-total span:last-child{font-variant-numeric:tabular-nums}
    .notes-block{margin-top:32px;padding:16px;background:#f9f9f9;border-left:3px solid #000;border-radius:4px}
    .notes-label{font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#999;margin-bottom:6px}
    .notes-text{font-size:12px;color:#444;line-height:1.7}
    .footer{margin-top:48px;padding-top:20px;border-top:1px solid #ddd;text-align:center;font-size:11px;color:#aaa}
    @media print{body{padding:20mm 22mm}@page{margin:0}}
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="logo">U<span>creative</span></div>
      <div style="font-size:12px;color:#888;margin-top:4px;line-height:1.8">
        Montréal, Québec<br/>
        info@ucreative.ca
      </div>
    </div>
    <div class="inv-meta">
      <h2>FACTURE</h2>
      <div class="number">${inv.number || 'INV-------'}</div>
      <div class="date">Émise le ${today}</div>
      ${inv.dueDate ? `<div class="date">Échéance : ${new Date(inv.dueDate).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</div>` : ''}
      <div><span class="status-badge">${statusLabel}</span></div>
    </div>
  </div>

  <div class="two-col">
    <div>
      <div class="section-label">Facturé à</div>
      <div class="client-name">${inv.clientName || ''}</div>
      <div class="client-detail">
        ${inv.clientCompany ? inv.clientCompany + '<br/>' : ''}
        ${inv.clientEmail   ? inv.clientEmail   + '<br/>' : ''}
        ${inv.clientPhone   ? inv.clientPhone               : ''}
      </div>
    </div>
    <div>
      <div class="section-label">De</div>
      <div class="from-info">
        <strong>Ucreative</strong><br/>
        Montréal, Québec<br/>
        info@ucreative.ca
      </div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th>Qté</th>
        <th>Prix/u</th>
        <th>Montant</th>
      </tr>
    </thead>
    <tbody>
      ${lines.map(l => `
        <tr>
          <td>${l.desc || ''}</td>
          <td>${parseFloat(l.qty) || 1}</td>
          <td>$${fmtCA(l.unitPrice)}</td>
          <td>$${fmtCA((parseFloat(l.qty) || 1) * (parseFloat(l.unitPrice) || 0))}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="totals">
    <div class="totals-row"><span>Sous-total</span><span>$${fmtCA(subtotal)}</span></div>
    ${inv.taxable !== false && tpsAmt > 0 ? `
    <div class="totals-row"><span>TPS (5 %)</span><span>$${fmtCA(tpsAmt)}</span></div>
    <div class="totals-row"><span>TVQ (9,975 %)</span><span>$${fmtCA(tvqAmt)}</span></div>
    ` : ''}
    <hr class="totals-divider"/>
    <div class="totals-total"><span>TOTAL</span><span>$${fmtCA(total)} CAD</span></div>
  </div>

  ${inv.notes ? `
  <div class="notes-block">
    <div class="notes-label">Notes</div>
    <div class="notes-text">${inv.notes}</div>
  </div>` : ''}

  <div class="footer">Merci pour votre confiance — Ucreative · Montréal, Québec · info@ucreative.ca</div>

  <script>window.onload=function(){window.print();setTimeout(()=>window.close(),1500);}</script>
</body>
</html>`;

  const w = window.open('', '_blank', 'width=900,height=700');
  if (w) {
    w.document.write(html);
    w.document.close();
  }
}

const CAT_LABELS = {
  design:      'Design graphique',
  vinyl:       'Impression vinyle',
  impression:  'Impression standard',
  web:         'Conception web',
  maintenance: 'Maintenance & hébergement',
  autre:       'Autre',
};

const inp = {
  base: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6,
    padding: '7px 10px',
    color: '#F0F0F0',
    fontSize: '0.84rem',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
  },
};

const sectionLabel = {
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#444',
  marginBottom: 10,
  display: 'block',
};

const fmt = (n) =>
  (parseFloat(n) || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InvoiceModal({ iModal, setIModal, clients, availableItems, onSave, onClose }) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const lines  = iModal.lines  || [{ desc: '', qty: 1, unitPrice: '' }];
  const taxable = iModal.taxable !== false;

  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unitPrice) || 0), 0);
  const tpsAmt   = taxable ? subtotal * TPS_RATE : 0;
  const tvqAmt   = taxable ? subtotal * TVQ_RATE : 0;
  const total    = subtotal + tpsAmt + tvqAmt;

  const patch = (obj) => setIModal(m => ({ ...m, ...obj }));
  const patchLine = (i, obj) => {
    const next = lines.map((l, idx) => idx === i ? { ...l, ...obj } : l);
    patch({ lines: next });
  };
  const addLine = () => patch({ lines: [...lines, { desc: '', qty: 1, unitPrice: '' }] });
  const removeLine = (i) => patch({ lines: lines.filter((_, idx) => idx !== i) });

  const addItemAsLine = (item) => {
    const line = {
      desc: item.name || item.label || '',
      qty: 1,
      unitPrice: item.price != null ? String(item.price) : '',
    };
    patch({ lines: [...lines, line] });
  };

  const selectClient = (id) => {
    const c = clients.find(cl => cl.id === id);
    if (!c) return;
    patch({
      clientName:    c.name    || '',
      clientEmail:   c.email   || '',
      clientPhone:   c.phone   || '',
      clientCompany: c.company || '',
    });
  };

  const grouped = useMemo(() => {
    const q = pickerSearch.toLowerCase();
    const items = availableItems.filter(it =>
      !q || (it.name || it.label || '').toLowerCase().includes(q)
    );
    return items.reduce((acc, it) => {
      const cat = it.category || 'autre';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(it);
      return acc;
    }, {});
  }, [availableItems, pickerSearch]);

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  const fieldStyle = { ...inp.base };
  const focusStyle = (e) => { e.target.style.borderColor = 'rgba(200,255,0,0.4)'; };
  const blurStyle  = (e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 500,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 16px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0e0e12',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
          width: '100%',
          maxWidth: 780,
          flexShrink: 0,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── HEADER ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '24px 28px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#EAEAEA', letterSpacing: '-0.02em', margin: 0 }}>
            {iModal.id ? 'Modifier la facture' : 'Nouvelle facture'}
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#444', cursor: 'pointer', fontSize: '1.5rem', lineHeight: 1, padding: 4 }}
          >×</button>
        </div>

        <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 28 }}>

          {/* ── SECTION CLIENT ── */}
          <div>
            <span style={sectionLabel}>Client</span>

            {clients && clients.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ ...sectionLabel, marginBottom: 5 }}>Sélectionner un client existant</label>
                <select
                  onChange={e => selectClient(e.target.value)}
                  defaultValue=""
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    color: '#EAEAEA',
                    fontSize: '0.88rem',
                    outline: 'none',
                    appearance: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="" style={{ background: '#111' }}>— Choisir un client —</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id} style={{ background: '#111' }}>
                      {c.name}{c.company ? ` — ${c.company}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
              {[
                ['Nom *',      'clientName',    'Jean Tremblay'],
                ['Courriel',   'clientEmail',   'jean@exemple.com'],
                ['Téléphone',  'clientPhone',   '514-555-1234'],
                ['Entreprise', 'clientCompany', 'ACME inc.'],
              ].map(([label, key, ph]) => (
                <div key={key}>
                  <label style={{ ...sectionLabel, marginBottom: 4 }}>{label}</label>
                  <input
                    value={iModal[key] || ''}
                    onChange={e => patch({ [key]: e.target.value })}
                    placeholder={ph}
                    style={fieldStyle}
                    onFocus={focusStyle}
                    onBlur={blurStyle}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* ── SECTION LIGNES ── */}
          <div>
            <span style={sectionLabel}>Lignes de facturation</span>

            {lines.length === 0 ? (
              <div style={{
                padding: '32px',
                textAlign: 'center',
                color: '#333',
                fontSize: '0.85rem',
                border: '1px dashed rgba(255,255,255,0.06)',
                borderRadius: 10,
                marginBottom: 12,
              }}>
                Aucune ligne — ajoutez des articles ci-dessous
              </div>
            ) : (
              <div style={{
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 10,
                overflow: 'hidden',
                marginBottom: 12,
              }}>
                {/* Table header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 70px 100px 90px 32px',
                  gap: 8,
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.03)',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}>
                  {['Description', 'Qté', 'Prix/u', 'Sous-total', ''].map(h => (
                    <div key={h} style={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#333',
                    }}>{h}</div>
                  ))}
                </div>

                {/* Table rows */}
                {lines.map((line, i) => {
                  const lineTotal = (parseFloat(line.qty) || 0) * (parseFloat(line.unitPrice) || 0);
                  return (
                    <div key={i} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 70px 100px 90px 32px',
                      gap: 8,
                      padding: '8px 12px',
                      borderBottom: i < lines.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                      alignItems: 'center',
                    }}>
                      <input
                        value={line.desc || ''}
                        onChange={e => patchLine(i, { desc: e.target.value })}
                        placeholder="Description du service..."
                        style={fieldStyle}
                        onFocus={focusStyle}
                        onBlur={blurStyle}
                      />
                      <input
                        type="number"
                        min="1"
                        value={line.qty || ''}
                        onChange={e => patchLine(i, { qty: e.target.value })}
                        placeholder="1"
                        style={{ ...fieldStyle, textAlign: 'center' }}
                        onFocus={focusStyle}
                        onBlur={blurStyle}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.unitPrice || ''}
                        onChange={e => patchLine(i, { unitPrice: e.target.value })}
                        placeholder="0.00"
                        style={{ ...fieldStyle, textAlign: 'right' }}
                        onFocus={focusStyle}
                        onBlur={blurStyle}
                      />
                      <div style={{
                        fontSize: '0.84rem',
                        color: lineTotal > 0 ? '#EAEAEA' : '#333',
                        fontWeight: lineTotal > 0 ? 700 : 400,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        ${fmt(lineTotal)}
                      </div>
                      <button
                        onClick={() => removeLine(i)}
                        style={{
                          background: 'none',
                          border: '1px solid rgba(248,113,113,0.2)',
                          borderRadius: 5,
                          color: '#f87171',
                          cursor: 'pointer',
                          width: 28,
                          height: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                          flexShrink: 0,
                        }}
                      >×</button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Picker + manual buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: showPicker ? 12 : 0 }}>
              <button
                onClick={() => setShowPicker(v => !v)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: showPicker ? 'rgba(200,255,0,0.1)' : 'transparent',
                  color: showPicker ? 'var(--accent,#C8FF00)' : '#555',
                  border: `1px solid ${showPicker ? 'rgba(200,255,0,0.3)' : 'rgba(255,255,255,0.08)'}`,
                  padding: '7px 14px', borderRadius: 7, fontWeight: 700,
                  fontSize: '0.78rem', cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: '1rem', lineHeight: 1 }}>+</span> Ajouter depuis catalogue
              </button>
              <button
                onClick={addLine}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'transparent', color: '#555',
                  border: '1px solid rgba(255,255,255,0.08)',
                  padding: '7px 14px', borderRadius: 7, fontWeight: 700,
                  fontSize: '0.78rem', cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: '1rem', lineHeight: 1 }}>+</span> Ligne manuelle
              </button>
            </div>

            {/* Item picker panel */}
            {showPicker && (
              <div style={{
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.02)',
                overflow: 'hidden',
              }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <input
                    value={pickerSearch}
                    onChange={e => setPickerSearch(e.target.value)}
                    placeholder="Rechercher un article..."
                    style={{ ...fieldStyle }}
                    onFocus={focusStyle}
                    onBlur={blurStyle}
                  />
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', padding: '8px 0' }}>
                  {Object.keys(grouped).length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#333', fontSize: '0.82rem' }}>
                      {pickerSearch ? 'Aucun article trouvé' : 'Aucun article dans le catalogue'}
                    </div>
                  ) : (
                    Object.entries(grouped).map(([cat, items]) => (
                      <div key={cat}>
                        <div style={{
                          padding: '6px 14px 4px',
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: '#555',
                        }}>
                          {CAT_LABELS[cat] || cat}
                        </div>
                        {items.map((item, idx) => (
                          <button
                            key={item.id || idx}
                            onClick={() => { addItemAsLine(item); setShowPicker(false); setPickerSearch(''); }}
                            style={{
                              display: 'flex',
                              width: '100%',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 14px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'background 0.1s',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = 'rgba(200,255,0,0.06)';
                              e.currentTarget.style.borderLeft = '2px solid rgba(200,255,0,0.4)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.borderLeft = 'none';
                            }}
                          >
                            <span style={{ color: '#EAEAEA', fontSize: '0.84rem', fontWeight: 600 }}>
                              {item.name || item.label || '—'}
                            </span>
                            <span style={{ color: '#888', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', flexShrink: 0, marginLeft: 12 }}>
                              {item.price != null ? `$${fmt(item.price)}` : '—'}
                            </span>
                          </button>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── SECTION OPTIONS ── */}
          <div>
            <span style={sectionLabel}>Options</span>

            <div style={{ marginBottom: 14 }}>
              <label style={{ ...sectionLabel, marginBottom: 5 }}>Notes internes</label>
              <textarea
                value={iModal.notes || ''}
                onChange={e => patch({ notes: e.target.value })}
                placeholder="Remarques, conditions particulières..."
                rows={3}
                style={{
                  ...fieldStyle,
                  resize: 'vertical',
                  minHeight: 72,
                  fontFamily: 'inherit',
                }}
                onFocus={focusStyle}
                onBlur={blurStyle}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 14 }}>
              <div>
                <label style={{ ...sectionLabel, marginBottom: 5 }}>Échéance</label>
                <input
                  type="date"
                  value={iModal.dueDate || ''}
                  onChange={e => patch({ dueDate: e.target.value })}
                  style={{ ...fieldStyle, colorScheme: 'dark' }}
                  onFocus={focusStyle}
                  onBlur={blurStyle}
                />
              </div>
              <div>
                <label style={{ ...sectionLabel, marginBottom: 5 }}>Statut</label>
                <select
                  value={iModal.status || 'en_attente'}
                  onChange={e => patch({ status: e.target.value })}
                  style={{
                    ...fieldStyle,
                    appearance: 'none',
                    cursor: 'pointer',
                    padding: '7px 10px',
                  }}
                >
                  <option value="en_attente" style={{ background: '#111' }}>En attente</option>
                  <option value="payé"       style={{ background: '#111' }}>Payé</option>
                  <option value="en_retard"  style={{ background: '#111' }}>En retard</option>
                </select>
              </div>
            </div>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
              userSelect: 'none',
              padding: '10px 14px',
              background: taxable ? 'rgba(200,255,0,0.05)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${taxable ? 'rgba(200,255,0,0.2)' : 'rgba(255,255,255,0.06)'}`,
              borderRadius: 8,
              transition: 'all 0.15s',
            }}>
              <input
                type="checkbox"
                checked={taxable}
                onChange={e => patch({ taxable: e.target.checked })}
                style={{ width: 16, height: 16, accentColor: '#C8FF00', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.84rem', color: taxable ? '#EAEAEA' : '#555', fontWeight: 600 }}>
                Appliquer les taxes — TPS + TVQ (Québec)
              </span>
              {taxable && (
                <span style={{
                  marginLeft: 'auto',
                  fontSize: '0.72rem',
                  color: 'var(--accent,#C8FF00)',
                  fontWeight: 700,
                }}>
                  5% + 9,975%
                </span>
              )}
            </label>
          </div>

          {/* ── SECTION TOTAUX ── */}
          <div>
            <span style={sectionLabel}>Totaux</span>
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 10,
              padding: '16px 20px',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: 340 }}>
                  <span style={{ color: '#555', fontSize: '0.84rem' }}>Sous-total</span>
                  <span style={{ color: '#EAEAEA', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    ${fmt(subtotal)}
                  </span>
                </div>

                {taxable && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: 340 }}>
                      <span style={{ color: '#555', fontSize: '0.84rem' }}>TPS (5%)</span>
                      <span style={{ color: '#888', fontVariantNumeric: 'tabular-nums', fontSize: '0.84rem' }}>
                        ${fmt(tpsAmt)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: 340 }}>
                      <span style={{ color: '#555', fontSize: '0.84rem' }}>TVQ (9,975%)</span>
                      <span style={{ color: '#888', fontVariantNumeric: 'tabular-nums', fontSize: '0.84rem' }}>
                        ${fmt(tvqAmt)}
                      </span>
                    </div>
                  </>
                )}

                <div style={{
                  width: '100%',
                  maxWidth: 340,
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  paddingTop: 10,
                  marginTop: 4,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                }}>
                  <span style={{ color: '#888', fontSize: '0.84rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Total</span>
                  <span style={{ fontSize: 24, fontWeight: 900, color: 'var(--accent,#C8FF00)', fontVariantNumeric: 'tabular-nums' }}>
                    ${fmt(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ── FOOTER ── */}
        <div style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 28px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
        }}>
          {/* PDF/Print button on the left */}
          <button
            onClick={() => {
              const lns = (iModal.lines || []).filter(l => l.desc && parseFloat(l.unitPrice) > 0);
              printInvoice({ ...iModal, lines: lns, subtotal, tps: tpsAmt, tvq: tvqAmt, total });
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'rgba(255,255,255,0.04)', color: '#888',
              border: '1px solid rgba(255,255,255,0.1)',
              padding: '9px 16px', borderRadius: 8,
              fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Aperçu / PDF
          </button>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: 'transparent', color: '#555',
                border: '1px solid rgba(255,255,255,0.08)',
                padding: '9px 18px', borderRadius: 8,
                fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
              }}
            >Annuler</button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: 'var(--accent,#C8FF00)', color: '#000',
                border: 'none', padding: '9px 20px', borderRadius: 8,
                fontWeight: 800, fontSize: '0.82rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving
                ? <><span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/> En cours...</>
                : 'Enregistrer'
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
